// src/pages/business/BusinessPage.tsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import BusinessApiService from "../../services/BusinessApiService";
import { useBlockchainAccess } from "../../hooks/useBlockchainAccess";

const API_BASE = (
  process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || ""
).replace("/admin", "");

interface BusinessProfile {
  _id: string; businessId: string; companyName: string; businessType: string;
  email: string; phone?: string; website?: string; industry?: string;
  description?: string; logo?: string; verified: boolean; status: string;
  address?: { street?: string; city?: string; state?: string; country?: string; zipCode?: string; };
  gs1?: { companyPrefix?: string; gln?: string; isOwnPrefix?: boolean; verificationStatus?: string; };
  plan?: { type?: string; name?: string; maxTags?: number; maxProducts?: number; maxStores?: number; };
  stats?: { totalTags?: number; totalProducts?: number; totalStores?: number; totalScans?: number; };
  createdAt?: string;
}

const BusinessPage: React.FC = () => {
  const [business, setBusiness] = useState<BusinessProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<BusinessProfile>>({});
  const blockchain = useBlockchainAccess();

  useEffect(() => { fetchBusiness(); }, []);

  const fetchBusiness = async () => {
    try {
      setLoading(true);
      const token = BusinessApiService.getBusinessToken();
      const res = await fetch(`${API_BASE}/api/business/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to load profile");
      const data = await res.json();
      const b = data.business || data;
      setBusiness(b);
      setForm({ companyName: b.companyName, phone: b.phone||"", website: b.website||"",
        industry: b.industry||"", description: b.description||"", address: b.address||{} });
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      setSaving(true); setSaveMsg(null);
      const token = BusinessApiService.getBusinessToken();
      const res = await fetch(`${API_BASE}/api/business/profile`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Failed to save");
      setSaveMsg("✅ Profile updated!"); setEditing(false); fetchBusiness();
    } catch (e: any) { setSaveMsg("❌ " + e.message); }
    finally { setSaving(false); }
  };

  const sf = (val: Partial<BusinessProfile>) => setForm(p => ({ ...p, ...val }));
  const sfa = (val: any) => setForm(p => ({ ...p, address: { ...p.address, ...val } }));

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"/></div>;
  if (error) return <div className="p-6 text-red-600 bg-red-50 rounded-lg m-6">❌ {error} <button onClick={fetchBusiness} className="ml-4 underline text-blue-600">Retry</button></div>;
  if (!business) return null;

  const planColor = business.plan?.type === "premium" ? "border-purple-200 bg-purple-50 text-purple-700"
    : business.plan?.type === "enterprise" ? "border-yellow-200 bg-yellow-50 text-yellow-700"
    : "border-blue-200 bg-blue-50 text-blue-700";

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Business Profile</h1>
        <div className="flex gap-2">
          {editing ? (<>
            <button onClick={() => { setEditing(false); setSaveMsg(null); }} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">{saving ? "Saving..." : "Save Changes"}</button>
          </>) : (
            <button onClick={() => setEditing(true)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">✏️ Edit Profile</button>
          )}
        </div>
      </div>

      {saveMsg && <div className={`p-3 rounded-lg text-sm ${saveMsg.startsWith("✅") ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>{saveMsg}</div>}

      {/* Company Info */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-start gap-4 mb-6">
          {business.logo
            ? <img src={business.logo} alt="Logo" className="w-16 h-16 rounded-xl object-cover border"/>
            : <div className="w-16 h-16 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600 text-2xl font-bold">{business.companyName?.charAt(0)}</div>
          }
          <div className="flex-1">
            {editing
              ? <input value={form.companyName||""} onChange={e=>sf({companyName:e.target.value})} className="text-xl font-bold w-full border rounded-lg px-3 py-1 mb-1"/>
              : <h2 className="text-xl font-bold text-gray-900">{business.companyName}</h2>
            }
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="text-sm text-gray-500 capitalize">{business.businessType}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${business.verified ? "bg-green-50 text-green-700 border-green-200" : "bg-yellow-50 text-yellow-700 border-yellow-200"}`}>{business.verified ? "✅ Verified" : "⏳ Pending"}</span>
              <span className="text-xs px-2 py-0.5 rounded-full border bg-gray-50 text-gray-600 capitalize">{business.status}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="text-xs font-medium text-gray-500 uppercase">Business ID</label><p className="text-sm font-mono text-gray-700 mt-0.5">{business.businessId}</p></div>
          <div><label className="text-xs font-medium text-gray-500 uppercase">Email</label><p className="text-sm text-gray-700 mt-0.5">{business.email}</p></div>
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">Phone</label>
            {editing ? <input value={form.phone||""} onChange={e=>sf({phone:e.target.value})} className="text-sm w-full border rounded-lg px-3 py-1 mt-0.5" placeholder="+1 234 567 8900"/>
              : <p className="text-sm text-gray-700 mt-0.5">{business.phone||"—"}</p>}
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">Website</label>
            {editing ? <input value={form.website||""} onChange={e=>sf({website:e.target.value})} className="text-sm w-full border rounded-lg px-3 py-1 mt-0.5" placeholder="https://yoursite.com"/>
              : business.website ? <a href={business.website} target="_blank" rel="noreferrer" className="text-sm text-blue-600 underline mt-0.5 block">{business.website}</a>
              : <p className="text-sm text-gray-400 mt-0.5">—</p>}
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">Industry</label>
            {editing ? <input value={form.industry||""} onChange={e=>sf({industry:e.target.value})} className="text-sm w-full border rounded-lg px-3 py-1 mt-0.5"/>
              : <p className="text-sm text-gray-700 mt-0.5">{business.industry||"—"}</p>}
          </div>
          <div><label className="text-xs font-medium text-gray-500 uppercase">Member Since</label><p className="text-sm text-gray-700 mt-0.5">{business.createdAt ? new Date(business.createdAt).toLocaleDateString() : "—"}</p></div>
          {editing && <div className="md:col-span-2"><label className="text-xs font-medium text-gray-500 uppercase">Description</label><textarea value={form.description||""} onChange={e=>sf({description:e.target.value})} className="text-sm w-full border rounded-lg px-3 py-2 mt-0.5" rows={3} placeholder="Describe your business..."/></div>}
        </div>
      </div>

      {/* Address */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">📍 Address</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[{key:"street",label:"Street"},{key:"city",label:"City"},{key:"state",label:"State"},{key:"country",label:"Country"},{key:"zipCode",label:"ZIP Code"}].map(({key,label})=>(
            <div key={key}><label className="text-xs font-medium text-gray-500 uppercase">{label}</label>
              {editing ? <input value={(form.address as any)?.[key]||""} onChange={e=>sfa({[key]:e.target.value})} className="text-sm w-full border rounded-lg px-3 py-1 mt-0.5"/>
                : <p className="text-sm text-gray-700 mt-0.5">{(business.address as any)?.[key]||"—"}</p>}
            </div>
          ))}
        </div>
      </div>

      {/* Plan */}
      {business.plan && (
        <div className={`rounded-xl border p-6 ${planColor}`}>
          <h3 className="font-semibold mb-4">⚡ Current Plan: <span className="capitalize font-bold">{business.plan.name||business.plan.type||"Basic"}</span></h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            {[{label:"Max Tags",val:business.plan.maxTags},{label:"Max Products",val:business.plan.maxProducts},{label:"Max Stores",val:business.plan.maxStores}].map(({label,val})=>(
              <div key={label} className="bg-white bg-opacity-50 rounded-lg p-3"><div className="text-lg font-bold">{val??"∞"}</div><div className="text-xs">{label}</div></div>
            ))}
          </div>
        </div>
      )}

      {/* Blockchain */}
      {!blockchain.loading && (
        blockchain.subscribed ? (
          // ─── Subscribed: show tier + usage + manage link ───
          <div className={`rounded-xl border p-6 ${
            blockchain.tier === 'enterprise' ? 'border-amber-200 bg-amber-50 text-amber-800'
            : blockchain.tier === 'premium'  ? 'border-purple-200 bg-purple-50 text-purple-800'
            : 'border-blue-200 bg-blue-50 text-blue-800'
          }`}>
            <div className="flex items-start justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2">
                {blockchain.tier === 'enterprise' ? '👑'
                 : blockchain.tier === 'premium' ? '⚡' : '🛡️'} Blockchain Verification
                <span className="text-xs px-2 py-0.5 rounded-full bg-white bg-opacity-50 capitalize font-bold">
                  {blockchain.tier}
                </span>
                {!blockchain.enabled && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">⏸️ Paused</span>
                )}
              </h3>
              <Link
                to="/business/blockchain"
                className="text-xs px-3 py-1.5 bg-white bg-opacity-70 rounded-lg font-medium hover:bg-opacity-100">
                ⚙ Manage
              </Link>
            </div>

            {blockchain.tier !== 'enterprise' && (
              <>
                <div className="flex items-center justify-between text-sm mb-2">
                  <span>Monthly anchors</span>
                  <span className="font-mono font-bold">
                    {blockchain.monthlyAnchorsUsed} / {blockchain.monthlyAnchorLimit}
                  </span>
                </div>
                <div className="w-full bg-white bg-opacity-50 h-2 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-current rounded-full transition-all"
                    style={{
                      width: `${Math.min(
                        ((blockchain.monthlyAnchorsUsed || 0) / Math.max(blockchain.monthlyAnchorLimit || 1, 1)) * 100,
                        100
                      )}%`
                    }} />
                </div>
                <p className="text-xs mt-2 opacity-75">
                  {blockchain.remainingAnchors} anchors remaining this month
                </p>
              </>
            )}

            {blockchain.tier === 'enterprise' && (
              <p className="text-sm">
                ♾️ Unlimited blockchain anchors. Used <span className="font-bold">{blockchain.monthlyAnchorsUsed}</span> this month.
              </p>
            )}
          </div>
        ) : (
          // ─── Free tier: show upsell card ───
          <div className="rounded-xl border-2 border-dashed border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50 p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-600 text-white flex items-center justify-center text-2xl flex-shrink-0">
                ⛓
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-purple-900 mb-1">Add Blockchain Verification</h3>
                <p className="text-sm text-purple-700 mb-3">
                  Make your products tamper-proof with on-chain authentication. Build customer trust with verifiable, immutable proof.
                </p>
                <Link
                  to="/business/blockchain"
                  className="inline-block px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700">
                  View Plans →
                </Link>
              </div>
            </div>
          </div>
        )
      )}

      {/* Stats */}
      {business.stats && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">📊 Usage Statistics</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[{label:"Tags",val:business.stats.totalTags,icon:"🏷️"},{label:"Products",val:business.stats.totalProducts,icon:"📦"},{label:"Stores",val:business.stats.totalStores,icon:"🏪"},{label:"Scans",val:business.stats.totalScans,icon:"📡"}].map(({label,val,icon})=>(
              <div key={label} className="bg-gray-50 rounded-xl p-4"><div className="text-2xl mb-1">{icon}</div><div className="text-xl font-bold text-gray-900">{val??0}</div><div className="text-xs text-gray-500">{label}</div></div>
            ))}
          </div>
        </div>
      )}

      {/* GS1 */}
      {business.gs1 && (business.gs1.companyPrefix || business.gs1.gln) && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">🔢 GS1 & Traceability
            <span className={`ml-2 text-xs px-2 py-0.5 rounded-full border ${business.gs1.isOwnPrefix ? "bg-green-50 text-green-700 border-green-200" : "bg-blue-50 text-blue-600 border-blue-200"}`}>
              {business.gs1.isOwnPrefix ? "✅ Own GS1 Prefix" : "🔵 JepTags Platform"}
            </span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {business.gs1.companyPrefix && <div><label className="text-xs font-medium text-gray-500 uppercase">Company Prefix</label><p className="text-sm font-mono text-gray-700 mt-0.5">{business.gs1.companyPrefix}</p></div>}
            {business.gs1.gln && <div><label className="text-xs font-medium text-gray-500 uppercase">GLN</label>
              <a href={`https://www.gs1.org/services/verified-by-gs1/results?gln=${encodeURIComponent(business.gs1.gln)}`} target="_blank" rel="noreferrer" className="text-sm text-blue-600 underline mt-0.5 block">{business.gs1.gln} 🔗</a>
            </div>}
            {business.gs1.verificationStatus && <div><label className="text-xs font-medium text-gray-500 uppercase">Verification</label><p className="text-sm text-gray-700 mt-0.5 capitalize">{business.gs1.verificationStatus}</p></div>}
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessPage;